import { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import useFetch from '../hooks/useFetch';
import { TemplateLoader } from '../template/TemplateLoader';
import type { VcardViewPayload, NotFoundPayload } from '../template/contract';
import { slugify } from '../utils/slug';
import Loading from './Loading';
import NotFound from './NotFound';

function EmployeeData() {
    const { urlKey } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { request, data, error } = useFetch<VcardViewPayload>();

    // Kanonischer Slug (lowercase). Weicht der aufgerufene Key davon ab
    // (z. B. alter/gedruckter Link `/Saskia_schwarzenberger`), leiten wir auf
    // die kanonische URL um (gleicher Host, Query-Params bleiben erhalten).
    // Der Redirect geht VOR dem Fetch: ist auch der kanonische Key unbekannt,
    // erscheint die Marken-Fehlerseite unter der kanonischen URL.
    const canonical = urlKey ? slugify(urlKey) : '';
    const needsRedirect = !!urlKey && !!canonical && canonical !== urlKey;

    useEffect(() => {
        if (needsRedirect) {
            const search = searchParams.toString();
            navigate(`/${canonical}${search ? `?${search}` : ''}`, { replace: true });
        }
    }, [needsRedirect, canonical, searchParams, navigate]);

    useEffect(() => {
        if (urlKey && !needsRedirect) {
            const preview = searchParams.get('preview');
            const previewQuery = preview ? `&preview=${encodeURIComponent(preview)}` : '';
            request(`vcard/context?key=${urlKey}${previewQuery}`);
        }
    }, [urlKey, needsRedirect, request, searchParams]);

    if (needsRedirect) return <Loading/>
    if (error) {
        // 404 → Marken-Fehlerseite INLINE, URL bleibt bestehen (kein navigate).
        if (error.status === 404 && error.body) {
            return <TemplateLoader payload={error.body as NotFoundPayload} view="error" />;
        }
        return <NotFound />;
    }
    if (!data) return <Loading/>

    return <TemplateLoader payload={data} view="vcard" />;
}

export default EmployeeData;
